import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';

import { IncrementsDuringGame, Reward, Wallet } from '@shared/models/wallet.model';
import { RealtimeService } from './realtime.service';
import { environment } from '@environments/environment';
import { LoggerService } from './logger.service';
import { GameService } from '@app/game/game.service';
import { ShopService } from './shop.service';
import { GlobalDataRepository } from '../state/global-data/global-data.repository';
import { WalletRepository } from '../state/wallet/wallet.repository';

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private http = inject(HttpClient);
  private realtimeService = inject(RealtimeService);
  logger = inject(LoggerService);
  private walletRepo = inject(WalletRepository);
  private gameService = inject(GameService);
  private shopService = inject(ShopService);
  private globalDataRepo = inject(GlobalDataRepository);

  private walletServiceUrl = `${environment.apiUrl}/api/wallet`;
  private walletIncrements: Wallet[] = [];

  get gameView(): boolean {
    return !!this.globalDataRepo.getCurrentGame();
  }

  constructor() {
    this.realtimeService.on('Wallet.Increment').subscribe({
      next: (wallet: Wallet) => {
        this.walletIncrements.push(wallet);

        if (!this.gameView || !wallet.increments.some((increment) => IncrementsDuringGame.includes(increment.reason))) {
          this.flushWalletIncrements();
        }
      },
      error: (error) => this.logger.error(error),
    });

    this.gameService.gameFinished$.subscribe(() => {
      this.flushWalletIncrements();
    });

    //Expose service from outside the app (BE & Native)
    window['angularComponentRef'] = { walletService: this, zone: { run: (fn: () => void) => fn() } };
  }

  hasReward(wallet: Reward): boolean {
    return !!wallet && (!!wallet.credits || !!wallet.gems);
  }

  public async refreshUserWallet(): Promise<void> {
    const wallet = await lastValueFrom(this.getUserWallet());
    this.walletRepo.setWallet({ totalCredits: wallet.credits, totalGems: wallet.gems, increments: [] });
  }

  private getUserWallet(): Observable<Reward> {
    return this.http.get<Reward>(this.walletServiceUrl);
  }

  private evaluateNotFunds(walletIncrements: Wallet[]): void {
    const gameProviderReserves = walletIncrements
      .flatMap((wallet) => wallet.increments)
      .filter((increment) => increment.reason.includes('GameProviderReserve'));

    if (
      gameProviderReserves.length &&
      walletIncrements[walletIncrements.length - 1].totalCredits <
        Math.abs(gameProviderReserves[gameProviderReserves.length - 1].credits)
    ) {
      this.shopService.notifyOutOfCredits();
    }
  }

  private flushWalletIncrements(): void {
    this.evaluateNotFunds(this.walletIncrements);

    while (this.walletIncrements.length) {
      const lastWallet = this.walletIncrements.shift();
      this.walletRepo.incrementWallet(lastWallet);
    }
  }
}
